/**
 * <s-product-carousel> — PDP media carousel.
 *
 * One slide shows at a time inside a native scroll-snap track, so touch swipe,
 * trackpad scroll and keyboard scrolling keep working without JavaScript.
 * This component adds previous/next/thumbnail commands, the "3 / 12" counter,
 * active-thumbnail state, and jumps to the selected variant's image.
 *
 * Commands (buttons with commandfor pointing at this element's id):
 *   --prev            previous slide
 *   --next            next slide
 *   --go              slide at the source button's data-index
 *
 * Required markup:
 *   [data-carousel-track]   scroll-snap container
 *   [data-carousel-slide]   one per media item, direct children of the track
 * Optional markup:
 *   [data-carousel-current] element whose text is the 1-based current index
 *   [data-carousel-thumb]   thumbnail buttons, matched to slides by data-index
 *   [data-carousel-prev] / [data-carousel-next] buttons disabled at the ends
 *   script[data-carousel-variant-media] JSON map of variant id -> slide index
 */
class SProductCarousel extends HTMLElement {
  #ac;
  #track = null;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #observer = null;
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-carousel-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-carousel-thumb]'));
    if (!this.#track || this.#slides.length === 0) return;

    const mapScript = this.querySelector('script[data-carousel-variant-media]');
    if (mapScript) {
      try {
        this.#variantMedia = JSON.parse(mapScript.textContent || '{}');
      } catch {
        this.#variantMedia = {};
      }
    }

    this.addEventListener('command', this.#handleCommand, opts);

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.#track,
      threshold: 0.6,
    });
    for (const slide of this.#slides) this.#observer.observe(slide);

    // Variant changes come from option inputs in the product form; the picker
    // writes the chosen variant id into input[name="id"] during the same event.
    const scope = this.closest('[data-product-page]');
    if (scope) {
      scope.addEventListener('change', this.#handleVariantChange, opts);
    }

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleCommand = (event) => {
    const command = event.command;
    if (command === '--prev') {
      this.goTo(this.#index - 1);
    } else if (command === '--next') {
      this.goTo(this.#index + 1);
    } else if (command === '--go') {
      const index = Number(event.source?.dataset.index);
      if (Number.isInteger(index)) this.goTo(index);
    }
  };

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.#slides.indexOf(entry.target);
      if (index !== -1 && index !== this.#index) {
        this.#index = index;
        this.#render();
      }
    }
  };

  #handleVariantChange = () => {
    const read = () => {
      const input = this.closest('[data-product-page]')?.querySelector('form input[name="id"]');
      const index = input ? this.#variantMedia[input.value] : undefined;
      if (Number.isInteger(index) && index !== this.#index) this.goTo(index);
    };
    requestAnimationFrame(read);
    // The picker may resolve the variant after a short fetch; check once more.
    setTimeout(read, 700);
  };

  goTo(index) {
    const count = this.#slides.length;
    const next = Math.max(0, Math.min(count - 1, index));
    const slide = this.#slides[next];
    if (!slide || !this.#track) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(this.#track).direction === 'rtl';
    const offset = slide.offsetLeft - this.#track.offsetLeft;
    this.#track.scrollTo({ left: isRtl ? offset : offset, behavior: reduceMotion ? 'auto' : 'smooth' });

    this.#index = next;
    this.#render();
  }

  #render() {
    const current = this.querySelector('[data-carousel-current]');
    if (current) current.textContent = String(this.#index + 1);

    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index === this.#slides.length - 1;

    this.#slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === this.#index ? 'false' : 'true');
      slide.toggleAttribute('inert', i !== this.#index);
    });

    for (const thumb of this.#thumbs) {
      const active = Number(thumb.dataset.index) === this.#index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) {
        const strip = thumb.parentElement?.closest('[data-carousel-thumbs]');
        if (strip) {
          const left = thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2;
          strip.scrollTo({ left, behavior: 'auto' });
        }
      }
    }
  }
}

if (!customElements.get('s-product-carousel')) {
  customElements.define('s-product-carousel', SProductCarousel);
}
